import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Mail, 
  MessageSquare, 
  Send, 
  Clock, 
  HelpCircle,
  Building2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import { generateContactPageSchema, generateOrganizationSchema, generateMultipleSchemas } from '@/lib/schema';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { BRAND_OPERATOR_LINE, COMPANY } from '@/lib/companyInfo';

const Contact = () => {
  const baseUrl = 'https://wageatlas.com';
  const pageUrl = `${baseUrl}/contact/`;
  const { toast } = useToast();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    inquiryType: 'support' as 'support' | 'business' | 'data' | 'other',
    website: '', // honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Generate structured data
  const contactPageSchema = generateContactPageSchema(
    'Contact Wage Atlas - Get in Touch',
    'Contact Wage Atlas for support, business inquiries, data questions, or general feedback. We respond within 24-48 hours.',
    pageUrl,
    baseUrl,
    [{ name: 'Home', url: '/' }, { name: 'Contact', url: '/contact' }]
  );

  const organizationSchema = generateOrganizationSchema(baseUrl);
  const structuredData = generateMultipleSchemas([contactPageSchema, organizationSchema]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Track form submission attempt
    if (typeof window !== 'undefined' && (window as unknown).gtag) {
      (window as unknown).gtag('event', 'contact_form_submit', {
        event_category: 'engagement',
        inquiry_type: formData.inquiryType,
      });
    }

    try {
      const { data, error } = await supabase.functions.invoke('api-contact-submit', {
        body: {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          inquiry_type: formData.inquiryType,
          user_id: user?.id || null,
          honeypot: formData.website,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to submit contact form');

      setIsSubmitting(false);
      setSubmitSuccess(true);
      toast({
        title: "Message sent!",
        description: "We'll get back to you within 24-48 hours.",
      });
      
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        inquiryType: 'support',
        website: '',
      });
    } catch (error: unknown) {
      console.error('Error submitting contact form:', error);
      setIsSubmitting(false);
      const errMsg = error instanceof Error ? error.message : "Failed to send message. Please try again or email us directly.";
      toast({
        title: "Error",
        description: errMsg,
        variant: "destructive",
      });
    }
  };

  const handleContactBusinessTeam = () => {
    setSubmitSuccess(false);
    setFormData(prev => ({ ...prev, inquiryType: 'business' }));
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SeoHead
        title="Contact Wage Atlas - Get in Touch"
        description="Contact Wage Atlas for support, business inquiries, data questions, or general feedback. We respond within 24-48 hours."
        canonical={pageUrl}
        schemas={structuredData}
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* Header */}
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <MessageSquare className="h-8 w-8 text-primary" />
                  <h1 className="text-3xl font-bold">Contact Us</h1>
                </div>
                <p className="text-lg text-muted-foreground">
                  Have a question, suggestion, or need help? We're here to assist you.
                </p>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {/* Contact Form */}
                <div className="lg:col-span-2">
                  <Card id="contact-form">
                    <CardHeader>
                      <CardTitle>Send us a Message</CardTitle>
                      <CardDescription>
                        Fill out the form below and we'll get back to you as soon as possible.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {submitSuccess ? (
                        <div className="text-center py-8 space-y-4">
                          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
                          <div>
                            <h3 className="text-lg font-semibold mb-2">Message Sent Successfully!</h3>
                            <p className="text-muted-foreground mb-4">
                              We've received your message and will respond within 24-48 hours.
                            </p>
                            <Button onClick={() => setSubmitSuccess(false)} variant="outline">
                              Send Another Message
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="inquiryType">Inquiry Type</Label>
                            <select
                              id="inquiryType"
                              name="inquiryType"
                              value={formData.inquiryType}
                              onChange={handleInputChange}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              required
                            >
                              <option value="support">General Support</option>
                              <option value="business">Business Inquiry</option>
                              <option value="data">Data Question</option>
                              <option value="other">Other</option>
                            </select>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="name">Name</Label>
                            <Input
                              id="name"
                              name="name"
                              type="text"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              placeholder="Your name"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              placeholder="your.email@example.com"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="subject">Subject</Label>
                            <Input
                              id="subject"
                              name="subject"
                              type="text"
                              value={formData.subject}
                              onChange={handleInputChange}
                              required
                              placeholder="Brief subject line"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="message">Message</Label>
                            <Textarea
                              id="message"
                              name="message"
                              value={formData.message}
                              onChange={handleInputChange}
                              required
                              rows={6}
                              placeholder="Tell us how we can help..."
                            />
                          </div>

                          {/* Honeypot field -- hidden from real users, catches bots */}
                          <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                            <Label htmlFor="website">Website</Label>
                            <Input
                              id="website"
                              name="website"
                              type="text"
                              value={formData.website}
                              onChange={handleInputChange}
                              tabIndex={-1}
                              autoComplete="off"
                            />
                          </div>

                          <Button 
                            type="submit" 
                            className="w-full" 
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>Sending...</>
                            ) : (
                              <>
                                <Send className="mr-2 h-4 w-4" />
                                Send Message
                              </>
                            )}
                          </Button>
                        </form>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Direct Contact Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Direct Contact</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Mail className="h-4 w-4 text-primary" />
                          <span className="font-medium">Support</span>
                        </div>
                        <a 
                          href={`mailto:${COMPANY.emails.support}`} 
                          className="text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                          {COMPANY.emails.support}
                        </a>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="h-4 w-4 text-primary" />
                          <span className="font-medium">Business</span>
                        </div>
                        <a 
                          href={`mailto:${COMPANY.emails.business}`} 
                          className="text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                          {COMPANY.emails.business}
                        </a>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <HelpCircle className="h-4 w-4 text-primary" />
                          <span className="font-medium">Data Questions</span>
                        </div>
                        <a 
                          href={`mailto:${COMPANY.emails.data}`} 
                          className="text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                          {COMPANY.emails.data}
                        </a>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Response Time */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Response Time
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        We typically respond to inquiries within <strong>24-48 hours</strong> during business days.
                      </p>
                    </CardContent>
                  </Card>

                  {/* FAQ Link */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Need Help?</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4">
                        Check out our support page for answers to common questions.
                      </p>
                      <Button asChild variant="outline" className="w-full">
                        <Link to="/support">
                          <HelpCircle className="mr-2 h-4 w-4" />
                          Visit Support
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Business Inquiries Section */}
              <Card className="bg-primary/5 border-primary/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    Business Inquiries
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    Interested in enterprise solutions, partnerships, or bulk data access? 
                    {COMPANY.legalName} offers custom solutions for teams, researchers, and organizations through the {COMPANY.brandName} brand.
                  </p>
                  <p className="text-sm text-muted-foreground">{BRAND_OPERATOR_LINE}</p>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <strong>Enterprise Features:</strong>
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                      <li>Bulk data exports and API access</li>
                      <li>Custom reporting and analysis</li>
                      <li>Dedicated support and account management</li>
                      <li>White-label solutions</li>
                    </ul>
                  </div>
                  <Button variant="outline" onClick={handleContactBusinessTeam}>
                    Contact Business Team
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
